import React, { useState, useEffect } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  WalletContainer,
  ContentContainer,
  PayoutModal,
  ModalOverlay,
  ModalContent,
  ModalInputContainer,
  ModalInput,
  ModalButtonContainer,
  ModalButton,
  DateSearch,
  // New Figma components
  FigmaWalletContainer,
  FigmaBalanceCard,
  FigmaAvailableBalance,
  FigmaBalanceTitle,
  FigmaBalanceAmount,
  FigmaPendingBalanceRow,
  FigmaBalanceItem,
  FigmaBalanceItemTitle,
  FigmaBalanceDivider,
  FigmaBalanceItemValue,
  FigmaRequestButton,
  FigmaTransactionSection,
  FigmaTransactionHeader,
  FigmaTransactionTitle,
  FigmaViewAllButton,
  FigmaTransactionList,
  FigmaTransactionItem,
  FigmaTransactionContent,
  FigmaTransactionRow,
  FigmaTransactionInfo,
  FigmaTransactionDetails,
  FigmaTransactionSource,
  FigmaTransactionTime,
  FigmaTransactionAmountSection,
  FigmaCreditTag,
  FigmaDebitTag,
  FigmaTransactionAmount,
  FigmaTransactionHistoryContainer,
  FigmaHistoryTitle,
  FigmaTypeSection,
  FigmaTypeLabel,
  FigmaDropdownContainer,
  FigmaDropdownText,
  FigmaDateSearchButton,
  FigmaScrollContainer,
  FigmaTransactionHistoryList,
  FigmaDateHeader,
  FigmaDateGroup,
  FigmaHistoryTransactionItem,
  FigmaHistoryTransactionContent,
  FigmaHistoryTransactionRow,
  FigmaHistoryProfileFrame,
  FigmaHistoryTransactionDetails,
  FigmaHistoryTransactionSource,
  FigmaHistoryTransactionTime,
  FigmaHistoryAmountSection,
  FigmaHistoryCreditTag,
  FigmaHistoryDebitTag,
  FigmaHistoryTransactionAmount,
  NoRecordsContainer,
  // Tablet and Desktop Components
  TabletWalletContainer,
  DesktopWalletContainer,
  TabletBalanceSection,
  DesktopBalanceSection,
  TabletBalanceCard,
  DesktopBalanceCard,
  TabletTransactionSection,
  DesktopTransactionSection,
  TabletTransactionHeader,
  DesktopTransactionHeader,
  TabletTransactionTitle,
  DesktopTransactionTitle,
  TabletFilterSection,
  DesktopFilterSection,
  TabletDateSearchButton,
  DesktopDateSearchButton,
  TabletTransactionGrid,
  DesktopTransactionList,
  TabletTransactionItem,
  DesktopTransactionItem,
  TabletRequestButton,
  DesktopRequestButton,
  TabletBalanceAmount,
  DesktopBalanceAmount,
  TabletBalanceRow,
  DesktopBalanceRow,
  TabletBalanceItem,
  DesktopBalanceItem,
  FigmaDropdownButton,
  FigmaDropdownMenu,
  FigmaDropdownItem,
  DesktopTransactionListOverview,
  TabletTransactionList,
  ModalCenterHeader,
  ModalMutedText,
  ModalGreenAmount,
  ModalTwoColSummary,
  ModalSummaryItem,
  ModalSummaryLabel,
  ModalSummaryValue,
  ModalFieldLabelOrange,
} from "../../styles/Wallet.styled";
import { POST } from "../../services/HTTPServices";
import { useErrorResponse } from "../../services/useErrorResponse";
import { ALERT_TITLES } from "../../constants";
import {
  alertMessage,
  alertMessageWithCancel,
} from "../../services/alertService";
import { useMediaQuery } from "@mui/material";
import {
  TabsContainer,
  Tab,
  TabsList,
  DesktopSearchButtonWallet,
  DatePickerWrapper,
  FilterLabel,
} from "../../styles/OrderHistory.styled";
import dayjs, { Dayjs } from "dayjs";
import { LanguageText } from "../../styles";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import moment from "moment";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  LoadingOverlay,
  LoadingSpinner,
  LoadingSpinnerIcon,
} from "../../styles/loading";

interface WalletData {
  payableBalance: number;
  availableBalance: number;
  totalBalance: number;
  todayTotalEarning: number;
  thisWeekTotalEarning: number;
  currency: string;
  status: number;
  lastSettlementDate: string | null;
}

interface Transaction {
  balance: number;
  creditAmount: number;
  debitAmount: number;
  description: string;
  transactionDateTime: string; // ISO date-time string
  transactionStatus: string;
}

type TabType = "overview" | "history";
type FilterType = "all" | "credit" | "debit";

const Wallet: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [walletData, setWalletData] = useState<WalletData | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<FilterType>("all");
  const [showDateSearch, setShowDateSearch] = useState(false);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutAccount, setPayoutAccount] = useState("");
  const [auth, setAuth] = React.useState<any>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const outErrorResponse = useErrorResponse();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(max-width:1024px)");

  // Group transactions by date
  const groupTransactionsByDate = (transactions: Transaction[]) => {
    const grouped = transactions.reduce((acc, transaction) => {
      const date = new Date(transaction.transactionDateTime);
      const dateKey = date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      });

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(transaction);
      return acc;
    }, {} as Record<string, Transaction[]>);

    return grouped;
  };

  // API functions
  const getWalletData = async (id: string) => {
    setLoading(true);
    const res: any = await POST(
      "account/GetServiceProviderAccountBalance",
      { userId: id },
      "payment"
    );
    if (res && res.status === 200) {
      setWalletData(res.data);
      setLoading(false);
    } else {
      setLoading(false);
      outErrorResponse(res.status, res.message);
    }
  };

  const getTransactionHistory = async (id: string = "") => {
    setLoading(true);

    const today = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(today.getFullYear() - 1);

    const payload = {
      userId: id,
      transactionType: filterType === "all" ? "" : filterType,
      fromDate: fromDate
        ? (fromDate as Dayjs).format("YYYY-MM-DD")
        : dayjs(oneYearAgo).format("YYYY-MM-DD"),
      toDate: toDate
        ? (toDate as Dayjs).format("YYYY-MM-DD")
        : dayjs(today).format("YYYY-MM-DD"),
    };

    const res: any = await POST(
      "serviceprovider/GetTransactionHistory",
      payload,
      "payment"
    );
    if (res && res.status === 200) {
      const apiDetails: Transaction[] = res.data.getTransactionDetails || [];

      const finalTransactions = apiDetails;

      if (activeTab === "overview") {
        setTransactions(finalTransactions.slice(0, 5));
      } else {
        setTransactions(finalTransactions);
      }
      setLoading(false);
    } else {
      setLoading(false);
      outErrorResponse(res.status, res.message);
    }
  };

  const requestPayout = async () => {
    try {
      const param = {
        amount: parseFloat(payoutAmount),
        payoutAccount: payoutAccount,
      };

      const res: any = await POST("wallet/RequestPayout", param, "provider");
      if (res && res.status === 200) {
        alertMessage(
          ALERT_TITLES.SUCCESS,
          "Payout request submitted successfully."
        );
        setShowPayoutModal(false);
        setPayoutAmount("");
        getWalletData(auth.id.toString()); // Refresh wallet data
      } else {
        outErrorResponse(res.status, res.message);
      }
    } catch (error) {
      console.error("Error requesting payout:", error);
    }
  };

  useEffect(() => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
    if (authUser) {
      setAuth(authUser);
      if (activeTab === "history") {
        getTransactionHistory(authUser.id.toString());
      } else {
        getWalletData(authUser.id.toString());
        getTransactionHistory(authUser.id.toString());

      }
    }
    setPayoutAccount("")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if(auth && auth.id){
      if (activeTab === "history") {
        getTransactionHistory(auth?.id?.toString());
      }else {
        getWalletData(auth?.id?.toString());
        getTransactionHistory(auth?.id?.toString());
      }
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filterType, fromDate, toDate]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };



  const handlePayoutRequest = async () => {
    if (!payoutAmount || parseFloat(payoutAmount) <= 0) {
      alertMessage(ALERT_TITLES.ERROR, "Please enter a valid amount.");
      return;
    }

    const confirmPayout = await alertMessageWithCancel(
      ALERT_TITLES.INFO,
      `Are you sure you want to request a payout of $${payoutAmount}?`
    );

    if (confirmPayout) {
      requestPayout();
    }
  };

  const handleViewAllTransactions = () => {
    setActiveTab("history");
  };

  const handleFilterChange = (newFilterType: FilterType) => {
    setFilterType(newFilterType);
    setShowDropdown(false);
  };

  const renderFigmaOverview = () => (
    <FigmaWalletContainer>
      <FigmaBalanceCard>
        <FigmaAvailableBalance>
          <FigmaBalanceTitle>Available Balance</FigmaBalanceTitle>
          <FigmaBalanceAmount>
            ${walletData?.availableBalance?.toFixed(2) || "0.00"}
          </FigmaBalanceAmount>
        </FigmaAvailableBalance>

        <FigmaPendingBalanceRow>
          <FigmaBalanceItem>
            <FigmaBalanceItemTitle>Pending Balance</FigmaBalanceItemTitle>
            <FigmaBalanceDivider />
            <FigmaBalanceItemValue>
              ${walletData?.payableBalance?.toFixed(2) || "20.00"}
            </FigmaBalanceItemValue>
          </FigmaBalanceItem>
          <FigmaBalanceItem>
            <FigmaBalanceItemTitle>Last Withdrawal Date</FigmaBalanceItemTitle>
            <FigmaBalanceDivider />
            <FigmaBalanceItemValue>
              {walletData?.lastSettlementDate || "03/01/2025"}
            </FigmaBalanceItemValue>
          </FigmaBalanceItem>
        </FigmaPendingBalanceRow>

        <FigmaRequestButton onClick={() => setShowPayoutModal(true)}>
          Request Payout
        </FigmaRequestButton>
      </FigmaBalanceCard>

      <FigmaTransactionSection>
        <FigmaTransactionHeader>
          <FigmaTransactionTitle>Recent Transactions</FigmaTransactionTitle>
          <FigmaViewAllButton onClick={handleViewAllTransactions}>
            View All
          </FigmaViewAllButton>
        </FigmaTransactionHeader>

        <FigmaTransactionList>
          {transactions.slice(0, 3).map((transaction) => (
            <FigmaTransactionItem key={transaction.transactionDateTime}>
              <FigmaTransactionContent>
                <FigmaTransactionRow>
                  <FigmaTransactionInfo>
                    <FigmaTransactionDetails>
                      <FigmaTransactionSource>
                        {transaction.description}
                      </FigmaTransactionSource>
                      <FigmaTransactionTime>
                        {moment(transaction.transactionDateTime).format("h:mm")}
                      </FigmaTransactionTime>
                    </FigmaTransactionDetails>
                  </FigmaTransactionInfo>
                  <FigmaTransactionAmountSection>
                    {transaction.creditAmount > 0 ? (
                      <FigmaCreditTag>Credit</FigmaCreditTag>
                    ) : (
                      <FigmaDebitTag>Debit</FigmaDebitTag>
                    )}
                    <FigmaTransactionAmount>
                      {transaction.creditAmount > 0 ? "+" : "-"}${" "}
                      {(transaction.creditAmount > 0
                        ? transaction.creditAmount
                        : transaction.debitAmount
                      ).toFixed(2)}
                    </FigmaTransactionAmount>
                  </FigmaTransactionAmountSection>
                </FigmaTransactionRow>
              </FigmaTransactionContent>
            </FigmaTransactionItem>
          ))}
        </FigmaTransactionList>
      </FigmaTransactionSection>
    </FigmaWalletContainer>
  );

  const renderTransactionHistory = () => {
    const groupedTransactions = groupTransactionsByDate(transactions);

    return (
      <FigmaTransactionHistoryContainer>
        <FigmaHistoryTitle>Transaction History</FigmaHistoryTitle>

        <FigmaTypeSection>
          <FigmaTypeLabel>Type</FigmaTypeLabel>

          <FigmaDropdownContainer>
            <FigmaDropdownButton onClick={() => setShowDropdown(!showDropdown)}>
              <FigmaDropdownText>
                {filterType === "all"
                  ? "All"
                  : filterType === "credit"
                  ? "Credit"
                  : "Debit"}
              </FigmaDropdownText>
              <KeyboardArrowDownIcon sx={{ color: "#E39435" }} />
            </FigmaDropdownButton>
            {showDropdown && (
              <FigmaDropdownMenu>
                <FigmaDropdownItem onClick={() => handleFilterChange("all")}>
                  All
                </FigmaDropdownItem>
                <FigmaDropdownItem onClick={() => handleFilterChange("credit")}>
                  Credit
                </FigmaDropdownItem>
                <FigmaDropdownItem onClick={() => handleFilterChange("debit")}>
                  Debit
                </FigmaDropdownItem>
              </FigmaDropdownMenu>
            )}
          </FigmaDropdownContainer>
        </FigmaTypeSection>

        <FigmaDateSearchButton
          onClick={() => setShowDateSearch(!showDateSearch)}
        >
          {showDateSearch ? <RemoveIcon /> : <AddIcon />}
          Date Search
        </FigmaDateSearchButton>

        {showDateSearch && (
          <DateSearch className="mb-16">
            <DatePickerWrapper>
              <FilterLabel>
                <LanguageText>From</LanguageText>
              </FilterLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label=""
                  value={fromDate}
                  onChange={valueChangedFrom}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        height: "40px",
                        "& .MuiOutlinedInput-root": {
                          height: "40px",
                          borderRadius: "8px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e39435",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e39435",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e39435",
                          },
                        },
                        "& .MuiInputBase-input": {
                          height: "40px",
                          padding: "0 14px",
                          boxSizing: "border-box",
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </DatePickerWrapper>

            <DatePickerWrapper>
              <FilterLabel>
                <LanguageText>To</LanguageText>
              </FilterLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label=""
                  value={toDate}
                  onChange={valueChangedTo}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        height: "40px",
                        "& .MuiOutlinedInput-root": {
                          height: "40px",
                          borderRadius: "8px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e39435",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e39435",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e39435",
                          },
                        },
                        "& .MuiInputBase-input": {
                          height: "40px",
                          padding: "0 14px",
                          boxSizing: "border-box",
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </DatePickerWrapper>
            <DesktopSearchButtonWallet onClick={search}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 23 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.54286 0C10.8086 0 12.9815 0.821783 14.5836 2.28457C16.1857 3.74735 17.0857 5.73131 17.0857 7.8C17.0857 9.732 16.3103 11.508 15.0354 12.876L15.3903 13.2H16.4286L23 19.2L21.0286 21L14.4571 15V14.052L14.1023 13.728C12.5521 14.9361 10.5809 15.5999 8.54286 15.6C6.27715 15.6 4.10424 14.7782 2.50214 13.3154C0.900048 11.8526 0 9.86869 0 7.8C0 5.73131 0.900048 3.74735 2.50214 2.28457C4.10424 0.821783 6.27715 0 8.54286 0ZM8.54286 2.4C5.25714 2.4 2.62857 4.8 2.62857 7.8C2.62857 10.8 5.25714 13.2 8.54286 13.2C11.8286 13.2 14.4571 10.8 14.4571 7.8C14.4571 4.8 11.8286 2.4 8.54286 2.4Z"
                  fill="white"
                />
              </svg>
              <LanguageText>Search</LanguageText>
            </DesktopSearchButtonWallet>
          </DateSearch>
        )}

        <FigmaScrollContainer>
          {transactions && transactions.length > 0 ? (
            <FigmaTransactionHistoryList>
              {Object.entries(groupedTransactions).map(
                ([date, dateTransactions]) => (
                  <FigmaDateGroup key={date}>
                    <FigmaDateHeader>{date}</FigmaDateHeader>
                    {dateTransactions.map((transaction) => (
                      <FigmaHistoryTransactionItem
                        key={transaction.transactionDateTime}
                      >
                        <FigmaHistoryTransactionContent>
                          <FigmaHistoryTransactionRow>
                            <FigmaHistoryProfileFrame>
                              <FigmaHistoryTransactionDetails>
                                <FigmaHistoryTransactionSource>
                                  {transaction.description}
                                </FigmaHistoryTransactionSource>
                                <FigmaHistoryTransactionTime>
                                  {moment(
                                    transaction.transactionDateTime
                                  ).format("h:mm")}
                                </FigmaHistoryTransactionTime>
                              </FigmaHistoryTransactionDetails>
                            </FigmaHistoryProfileFrame>
                            <FigmaHistoryAmountSection>
                              {transaction.creditAmount > 0 ? (
                                <FigmaHistoryCreditTag>
                                  Credit
                                </FigmaHistoryCreditTag>
                              ) : (
                                <FigmaHistoryDebitTag>
                                  Debit
                                </FigmaHistoryDebitTag>
                              )}
                              <FigmaHistoryTransactionAmount>
                                {transaction.creditAmount > 0 ? "+S$" : "-S$"}{" "}
                                {(transaction.creditAmount > 0
                                  ? transaction.creditAmount
                                  : transaction.debitAmount
                                )?.toFixed(2)}
                              </FigmaHistoryTransactionAmount>
                            </FigmaHistoryAmountSection>
                          </FigmaHistoryTransactionRow>
                        </FigmaHistoryTransactionContent>
                      </FigmaHistoryTransactionItem>
                    ))}
                  </FigmaDateGroup>
                )
              )}
            </FigmaTransactionHistoryList>
          ) : (
            <NoRecordsContainer>
              <LanguageText>No Transaction Records.</LanguageText>{" "}
            </NoRecordsContainer>
          )}
        </FigmaScrollContainer>
      </FigmaTransactionHistoryContainer>
    );
  };

  const renderTabletOverview = () => (
    <TabletBalanceSection>
      <TabletBalanceCard>
        <FigmaAvailableBalance>
          <FigmaBalanceTitle>Available Balance</FigmaBalanceTitle>
          <TabletBalanceAmount>
            ${walletData?.availableBalance?.toFixed(2) || "0.00"}
          </TabletBalanceAmount>
        </FigmaAvailableBalance>

        <TabletBalanceRow>
          <TabletBalanceItem>
            <FigmaBalanceItemTitle>Pending Balance</FigmaBalanceItemTitle>
            <FigmaBalanceDivider />
            <FigmaBalanceItemValue>
              ${walletData?.payableBalance?.toFixed(2) || "20.00"}
            </FigmaBalanceItemValue>
          </TabletBalanceItem>
          <TabletBalanceItem>
            <FigmaBalanceItemTitle>Last Withdrawal Date</FigmaBalanceItemTitle>
            <FigmaBalanceDivider />
            <FigmaBalanceItemValue>
              {walletData?.lastSettlementDate || "03/01/2025"}
            </FigmaBalanceItemValue>
          </TabletBalanceItem>
        </TabletBalanceRow>

        <TabletRequestButton onClick={() => setShowPayoutModal(true)}>
          Request Payout
        </TabletRequestButton>
      </TabletBalanceCard>

      <TabletTransactionHeader>
        <TabletTransactionTitle>Recent Transactions</TabletTransactionTitle>
        <FigmaViewAllButton onClick={handleViewAllTransactions}>
          View All
        </FigmaViewAllButton>
      </TabletTransactionHeader>

      <TabletTransactionGrid>
        {transactions.slice(0, 6).map((transaction) => (
          <TabletTransactionItem key={transaction.transactionDateTime}>
            <FigmaHistoryTransactionRow>
              <FigmaHistoryProfileFrame>
                <FigmaHistoryTransactionDetails>
                  <FigmaHistoryTransactionSource>
                    {transaction.description}
                  </FigmaHistoryTransactionSource>
                  <FigmaHistoryTransactionTime>
                    {moment(transaction.transactionDateTime).format("h:mm")}
                  </FigmaHistoryTransactionTime>
                </FigmaHistoryTransactionDetails>
              </FigmaHistoryProfileFrame>
              <FigmaHistoryAmountSection>
                {transaction.creditAmount > 0 ? (
                  <FigmaHistoryCreditTag>Credit</FigmaHistoryCreditTag>
                ) : (
                  <FigmaHistoryDebitTag>Debit</FigmaHistoryDebitTag>
                )}
                <FigmaHistoryTransactionAmount>
                  {transaction.creditAmount > 0 ? "+S$" : "-S$"}{" "}
                  {(transaction.creditAmount > 0
                    ? transaction.creditAmount
                    : transaction.debitAmount
                  )?.toFixed(2)}
                </FigmaHistoryTransactionAmount>
              </FigmaHistoryAmountSection>
            </FigmaHistoryTransactionRow>
          </TabletTransactionItem>
        ))}
      </TabletTransactionGrid>
    </TabletBalanceSection>
  );

  const renderDesktopOverview = () => (
    <>
      <DesktopBalanceSection>
        <DesktopBalanceCard>
          <FigmaAvailableBalance>
            <FigmaBalanceTitle>Available Balance</FigmaBalanceTitle>
            <DesktopBalanceAmount>
              ${walletData?.availableBalance?.toFixed(2) || "0.00"}
            </DesktopBalanceAmount>
          </FigmaAvailableBalance>

          <DesktopBalanceRow>
            <DesktopBalanceItem>
              <FigmaBalanceItemTitle>Pending Balance</FigmaBalanceItemTitle>
              <FigmaBalanceDivider />
              <FigmaBalanceItemValue>
                ${walletData?.payableBalance?.toFixed(2) || "-"}
              </FigmaBalanceItemValue>
            </DesktopBalanceItem>
            <DesktopBalanceItem>
              <FigmaBalanceItemTitle>
                Last Withdrawal Date
              </FigmaBalanceItemTitle>
              <FigmaBalanceDivider />
              <FigmaBalanceItemValue>
                {walletData?.lastSettlementDate || "-"}
              </FigmaBalanceItemValue>
            </DesktopBalanceItem>
          </DesktopBalanceRow>

          <DesktopRequestButton onClick={() => setShowPayoutModal(true)}>
            Request Payout
          </DesktopRequestButton>
        </DesktopBalanceCard>
      </DesktopBalanceSection>

      <DesktopTransactionSection>
        <DesktopTransactionHeader>
          <DesktopTransactionTitle>Recent Transactions</DesktopTransactionTitle>
          <FigmaViewAllButton onClick={handleViewAllTransactions}>
            View All
          </FigmaViewAllButton>
        </DesktopTransactionHeader>

        <DesktopTransactionListOverview>
          {transactions.map((transaction) => (
            <DesktopTransactionItem key={transaction.transactionDateTime}>
              <FigmaHistoryProfileFrame>
                <FigmaHistoryTransactionDetails>
                  <FigmaHistoryTransactionSource>
                    {transaction.description}
                  </FigmaHistoryTransactionSource>
                  <FigmaHistoryTransactionTime>
                    {moment(transaction.transactionDateTime).format("h:mm")}
                  </FigmaHistoryTransactionTime>
                </FigmaHistoryTransactionDetails>
              </FigmaHistoryProfileFrame>
              <FigmaHistoryAmountSection>
                {transaction.creditAmount > 0 ? (
                  <FigmaHistoryCreditTag>Credit</FigmaHistoryCreditTag>
                ) : (
                  <FigmaHistoryDebitTag>Debit</FigmaHistoryDebitTag>
                )}
                <FigmaHistoryTransactionAmount>
                  {transaction.creditAmount > 0 ? "+S$" : "-S$"}{" "}
                  {(transaction.creditAmount > 0
                    ? transaction.creditAmount
                    : transaction.debitAmount
                  )?.toFixed(2)}
                </FigmaHistoryTransactionAmount>
              </FigmaHistoryAmountSection>
            </DesktopTransactionItem>
          ))}
        </DesktopTransactionListOverview>
      </DesktopTransactionSection>
    </>
  );

  const renderTabletTransactionHistory = () => {
    const groupedTransactions = groupTransactionsByDate(transactions);

    return (
      <TabletTransactionSection>
        <TabletFilterSection>
          <FigmaDropdownContainer>
            <FigmaDropdownButton onClick={() => setShowDropdown(!showDropdown)}>
              <FigmaDropdownText>
                {filterType === "all"
                  ? "All"
                  : filterType === "credit"
                  ? "Credit"
                  : "Debit"}
              </FigmaDropdownText>
              <KeyboardArrowDownIcon sx={{ color: "#E39435" }} />
            </FigmaDropdownButton>
            {showDropdown && (
              <FigmaDropdownMenu>
                <FigmaDropdownItem onClick={() => handleFilterChange("all")}>
                  All
                </FigmaDropdownItem>
                <FigmaDropdownItem onClick={() => handleFilterChange("credit")}>
                  Credit
                </FigmaDropdownItem>
                <FigmaDropdownItem onClick={() => handleFilterChange("debit")}>
                  Debit
                </FigmaDropdownItem>
              </FigmaDropdownMenu>
            )}
          </FigmaDropdownContainer>

          <TabletDateSearchButton
            onClick={() => setShowDateSearch(!showDateSearch)}
          >
          {showDateSearch ? <RemoveIcon /> : <AddIcon />}
          Date Search
          </TabletDateSearchButton>
        </TabletFilterSection>

        {showDateSearch && (
            <DateSearch className="mb-16">
            <DatePickerWrapper>
              <FilterLabel>
                <LanguageText>From</LanguageText>
              </FilterLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label=""
                  value={fromDate}
                  onChange={valueChangedFrom}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        height: "40px",
                        "& .MuiOutlinedInput-root": {
                          height: "40px",
                          borderRadius: "8px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e39435",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e39435",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e39435",
                          },
                        },
                        "& .MuiInputBase-input": {
                          height: "40px",
                          padding: "0 14px",
                          boxSizing: "border-box",
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </DatePickerWrapper>

            <DatePickerWrapper>
              <FilterLabel>
                <LanguageText>To</LanguageText>
              </FilterLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label=""
                  value={toDate}
                  onChange={valueChangedTo}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        height: "40px",
                        "& .MuiOutlinedInput-root": {
                          height: "40px",
                          borderRadius: "8px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e39435",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e39435",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e39435",
                          },
                        },
                        "& .MuiInputBase-input": {
                          height: "40px",
                          padding: "0 14px",
                          boxSizing: "border-box",
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </DatePickerWrapper>
              <DesktopSearchButtonWallet onClick={search}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 23 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.54286 0C10.8086 0 12.9815 0.821783 14.5836 2.28457C16.1857 3.74735 17.0857 5.73131 17.0857 7.8C17.0857 9.732 16.3103 11.508 15.0354 12.876L15.3903 13.2H16.4286L23 19.2L21.0286 21L14.4571 15V14.052L14.1023 13.728C12.5521 14.9361 10.5809 15.5999 8.54286 15.6C6.27715 15.6 4.10424 14.7782 2.50214 13.3154C0.900048 11.8526 0 9.86869 0 7.8C0 5.73131 0.900048 3.74735 2.50214 2.28457C4.10424 0.821783 6.27715 0 8.54286 0ZM8.54286 2.4C5.25714 2.4 2.62857 4.8 2.62857 7.8C2.62857 10.8 5.25714 13.2 8.54286 13.2C11.8286 13.2 14.4571 10.8 14.4571 7.8C14.4571 4.8 11.8286 2.4 8.54286 2.4Z"
                    fill="white"
                  />
                </svg>
                <LanguageText>Search</LanguageText>
              </DesktopSearchButtonWallet>
            </DateSearch>
        )}

        <FigmaScrollContainer>
          {transactions && transactions.length > 0 ? (
            <FigmaTransactionHistoryList>
              {Object.entries(groupedTransactions).map(
                ([date, dateTransactions]) => (
                  <FigmaDateGroup key={date}>
                    <FigmaDateHeader>{date}</FigmaDateHeader>
                    <TabletTransactionList>
                      {dateTransactions.map((transaction) => (
                        <DesktopTransactionItem
                          key={transaction.transactionDateTime}
                        >
                          <FigmaHistoryProfileFrame>
                            <FigmaHistoryTransactionDetails>
                              <FigmaHistoryTransactionSource>
                                {transaction.description}
                              </FigmaHistoryTransactionSource>
                              <FigmaHistoryTransactionTime>
                                {moment(transaction.transactionDateTime).format(
                                  "h:mm"
                                )}
                              </FigmaHistoryTransactionTime>
                            </FigmaHistoryTransactionDetails>
                          </FigmaHistoryProfileFrame>
                          <FigmaHistoryAmountSection>
                            {transaction.creditAmount > 0 ? (
                              <FigmaHistoryCreditTag>
                                Credit
                              </FigmaHistoryCreditTag>
                            ) : (
                              <FigmaHistoryDebitTag>Debit</FigmaHistoryDebitTag>
                            )}
                            <FigmaHistoryTransactionAmount>
                              {transaction.creditAmount > 0 ? "+S$" : "-S$"}{" "}
                              {(transaction.creditAmount > 0
                                ? transaction.creditAmount
                                : transaction.debitAmount
                              )?.toFixed(2)}
                            </FigmaHistoryTransactionAmount>
                          </FigmaHistoryAmountSection>
                        </DesktopTransactionItem>
                      ))}
                    </TabletTransactionList>
                  </FigmaDateGroup>
                )
              )}
            </FigmaTransactionHistoryList>
          ) : (
            <NoRecordsContainer>No Transaction Records.</NoRecordsContainer>
          )}
        </FigmaScrollContainer>
      </TabletTransactionSection>
    );
  };

  const renderDesktopTransactionHistory = () => {
    const groupedTransactions = groupTransactionsByDate(transactions);

    return (
      <DesktopTransactionSection>
        <DesktopFilterSection>
          <FigmaDropdownContainer>
            <FigmaDropdownButton onClick={() => setShowDropdown(!showDropdown)}>
              <FigmaDropdownText>
                {filterType === "all"
                  ? "All"
                  : filterType === "credit"
                  ? "Credit"
                  : "Debit"}
              </FigmaDropdownText>
              <KeyboardArrowDownIcon sx={{ color: "#E39435" }} />
            </FigmaDropdownButton>
            {showDropdown && (
              <FigmaDropdownMenu>
                <FigmaDropdownItem onClick={() => handleFilterChange("all")}>
                  All
                </FigmaDropdownItem>
                <FigmaDropdownItem onClick={() => handleFilterChange("credit")}>
                  Credit
                </FigmaDropdownItem>
                <FigmaDropdownItem onClick={() => handleFilterChange("debit")}>
                  Debit
                </FigmaDropdownItem>
              </FigmaDropdownMenu>
            )}
          </FigmaDropdownContainer>

          <DesktopDateSearchButton
            onClick={() => setShowDateSearch(!showDateSearch)}
          >
            {showDateSearch ? <RemoveIcon /> : <AddIcon />}
            Date Search
          </DesktopDateSearchButton>
        </DesktopFilterSection>

        {showDateSearch && (
            <DateSearch className="mb-16">
              <DatePickerWrapper>
              <FilterLabel>
                <LanguageText>From</LanguageText>
              </FilterLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label=""
                  value={fromDate}
                  onChange={valueChangedFrom}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        height: "40px",
                        "& .MuiOutlinedInput-root": {
                          height: "40px",
                          borderRadius: "8px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e39435",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e39435",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e39435",
                          },
                        },
                        "& .MuiInputBase-input": {
                          height: "40px",
                          padding: "0 14px",
                          boxSizing: "border-box",
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </DatePickerWrapper>

            <DatePickerWrapper>
              <FilterLabel>
                <LanguageText>To</LanguageText>
              </FilterLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label=""
                  value={toDate}
                  onChange={valueChangedTo}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        height: "40px",
                        "& .MuiOutlinedInput-root": {
                          height: "40px",
                          borderRadius: "8px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e39435",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e39435",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e39435",
                          },
                        },
                        "& .MuiInputBase-input": {
                          height: "40px",
                          padding: "0 14px",
                          boxSizing: "border-box",
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </DatePickerWrapper>
              <DesktopSearchButtonWallet onClick={search}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 23 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.54286 0C10.8086 0 12.9815 0.821783 14.5836 2.28457C16.1857 3.74735 17.0857 5.73131 17.0857 7.8C17.0857 9.732 16.3103 11.508 15.0354 12.876L15.3903 13.2H16.4286L23 19.2L21.0286 21L14.4571 15V14.052L14.1023 13.728C12.5521 14.9361 10.5809 15.5999 8.54286 15.6C6.27715 15.6 4.10424 14.7782 2.50214 13.3154C0.900048 11.8526 0 9.86869 0 7.8C0 5.73131 0.900048 3.74735 2.50214 2.28457C4.10424 0.821783 6.27715 0 8.54286 0ZM8.54286 2.4C5.25714 2.4 2.62857 4.8 2.62857 7.8C2.62857 10.8 5.25714 13.2 8.54286 13.2C11.8286 13.2 14.4571 10.8 14.4571 7.8C14.4571 4.8 11.8286 2.4 8.54286 2.4Z"
                    fill="white"
                  />
                </svg>
                <LanguageText>Search</LanguageText>
              </DesktopSearchButtonWallet>
            </DateSearch>
        )}

        <FigmaScrollContainer>
          {transactions && transactions.length > 0 ? (
            <FigmaTransactionHistoryList>
              {Object.entries(groupedTransactions).map(
                ([date, dateTransactions]) => (
                  <FigmaDateGroup key={date}>
                    <FigmaDateHeader>{date}</FigmaDateHeader>
                    <DesktopTransactionList>
                      {dateTransactions.map((transaction) => (
                        <DesktopTransactionItem
                          key={transaction.transactionDateTime}
                        >
                          <FigmaHistoryProfileFrame>
                            <FigmaHistoryTransactionDetails>
                              <FigmaHistoryTransactionSource>
                                {transaction.description}
                              </FigmaHistoryTransactionSource>
                              <FigmaHistoryTransactionTime>
                                {moment(transaction.transactionDateTime).format(
                                  "h:mm"
                                )}
                              </FigmaHistoryTransactionTime>
                            </FigmaHistoryTransactionDetails>
                          </FigmaHistoryProfileFrame>
                          <FigmaHistoryAmountSection>
                            {transaction.creditAmount > 0 ? (
                              <FigmaHistoryCreditTag>
                                Credit
                              </FigmaHistoryCreditTag>
                            ) : (
                              <FigmaHistoryDebitTag>Debit</FigmaHistoryDebitTag>
                            )}
                            <FigmaHistoryTransactionAmount>
                              {transaction.creditAmount > 0 ? "+S$" : "-S$"}{" "}
                              {(transaction.creditAmount > 0
                                ? transaction.creditAmount
                                : transaction.debitAmount
                              )?.toFixed(2)}
                            </FigmaHistoryTransactionAmount>
                          </FigmaHistoryAmountSection>
                        </DesktopTransactionItem>
                      ))}
                    </DesktopTransactionList>
                  </FigmaDateGroup>
                )
              )}
            </FigmaTransactionHistoryList>
          ) : (
            <NoRecordsContainer>No Transaction Records.</NoRecordsContainer>
          )}
        </FigmaScrollContainer>
      </DesktopTransactionSection>
    );
  };
  const renderPayoutModal = () => (
    <PayoutModal $isOpen={showPayoutModal}>
      <ModalOverlay onClick={() => setShowPayoutModal(false)} />
      <ModalContent>
        <ModalCenterHeader>
          <ModalMutedText>Available Balance</ModalMutedText>
          <ModalGreenAmount>
            ${walletData?.availableBalance?.toFixed(2) || "0.00"}
          </ModalGreenAmount>
        </ModalCenterHeader>

        <ModalTwoColSummary>
          <ModalSummaryItem>
            <ModalSummaryLabel>Pending Balance</ModalSummaryLabel>
            <ModalSummaryValue>
              ${walletData?.payableBalance?.toFixed(2) || "-"}
            </ModalSummaryValue>
          </ModalSummaryItem>
          <ModalSummaryItem>
            <ModalSummaryLabel>Last Settlement Date</ModalSummaryLabel>
            <ModalSummaryValue>
              {walletData?.lastSettlementDate || "-"}
            </ModalSummaryValue>
          </ModalSummaryItem>
        </ModalTwoColSummary>

        <ModalInputContainer>
          <ModalFieldLabelOrange>Enter Amount *</ModalFieldLabelOrange>
          <ModalInput
            type="number"
            value={payoutAmount}
            onChange={(e) => setPayoutAmount(e.target.value)}
            placeholder="$500.00"
            step="0.01"
            min="0"
          />
        </ModalInputContainer>

        <ModalButtonContainer>
          <ModalButton
            $variant="secondary"
            onClick={() => setShowPayoutModal(false)}
          >
            Cancel
          </ModalButton>
          <ModalButton
            $variant="primary"
            onClick={handlePayoutRequest}
          >
            Request Payout
          </ModalButton>
        </ModalButtonContainer>
      </ModalContent>
    </PayoutModal>
  );

  const valueChangedFrom = (date: Dayjs | null) => {
    setFromDate(date);
  };

  const valueChangedTo = (date: Dayjs | null) => {
    setToDate(date);
  };

  const search = () => {
    getTransactionHistory(auth.id.toString());
  };

  return (
    <>
      {loading && (
        <LoadingOverlay>
          <LoadingSpinner>
            <LoadingSpinnerIcon />
          </LoadingSpinner>
        </LoadingOverlay>
      )}
      <WalletContainer>
        {!isMobile && (
          <TabsContainer className="mb-16">
            <TabsList>
              <Tab
                $isActive={activeTab === "overview"}
                onClick={() => handleTabChange("overview")}
              >
                Overview
              </Tab>
              <Tab
                $isActive={activeTab === "history"}
                onClick={() => handleTabChange("history")}
              >
                Transaction History
              </Tab>
            </TabsList>
          </TabsContainer>
        )}

        {isMobile ? (
          <ContentContainer>
            {activeTab === "overview"
              ? renderFigmaOverview()
              : renderTransactionHistory()}
          </ContentContainer>
        ) : isTablet ? (
          <TabletWalletContainer>
            {activeTab === "overview"
              ? renderTabletOverview()
              : renderTabletTransactionHistory()}
          </TabletWalletContainer>
        ) : (
          <DesktopWalletContainer>
            {activeTab === "overview"
              ? renderDesktopOverview()
              : renderDesktopTransactionHistory()}
          </DesktopWalletContainer>
        )}

        {renderPayoutModal()}
      </WalletContainer>
    </>
  );
};

export default Wallet;
